<template>
  <v-dialog v-model="dialog" persistent width="560">
    <v-card>
      <v-card-title>Transfer this Item</v-card-title>
      <v-card-text>
        <v-text-field
          readonly
          label="Item Name"
          :value="item.item_name"
        ></v-text-field>
        <!-- Serial-driven mode: the scanned serials decide the batches -->
        <div v-if="serialMode" class="mb-2">
          <p class="mb-1 font-weight-medium">
            Serials to transfer
            <span class="grey--text">(batches set by the scanned serials)</span>
          </p>
          <v-chip
            v-for="s in scannedSerials"
            :key="s.serial_number"
            class="ma-1"
            small
            color="primary"
            outlined
          >
            <v-icon x-small left>mdi-barcode</v-icon>
            {{ s.serial_number }}
            <span v-if="s.batch_description" class="grey--text ml-1"
              >({{ s.batch_description }})</span
            >
          </v-chip>
        </div>

        <!-- Lot selection (manual, non-serial transfers) -->
        <div v-else-if="lotsLoading" class="my-3 grey--text">
          Loading stock batches…
        </div>
        <div v-else-if="allocations.length" class="mb-2">
          <p class="mb-1 font-weight-medium">
            Select which batch(es) to transfer:
          </p>
          <v-simple-table dense>
            <thead>
              <tr>
                <th class="text-left">Batch</th>
                <th class="text-right">Available</th>
                <th class="text-right">Unit Cost</th>
                <th class="text-right" style="width: 130px">Qty to Transfer</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(alloc, idx) in allocations" :key="alloc.lot_id">
                <td>
                  Batch {{ alloc.lot_id
                  }}{{ alloc.batch ? " - " + alloc.batch : "" }}
                </td>
                <td class="text-right">{{ alloc.remaining }}</td>
                <td class="text-right">
                  ₱{{ Number(alloc.unit_cost).toLocaleString() }}
                </td>
                <td class="text-right">
                  <v-text-field
                    type="number"
                    min="0"
                    :max="alloc.remaining"
                    v-model.number="alloc.qty"
                    dense
                    hide-details
                    @input="clampAlloc(idx)"
                  ></v-text-field>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </div>
        <v-alert v-else type="warning" dense outlined class="my-3">
          This item has no stock batches available to transfer.
        </v-alert>

        <div class="d-flex">
          <v-text-field
            readonly
            label="Total Quantity"
            :value="totalAllocated"
            prepend-icon="mdi-box"
          ></v-text-field>
          <v-text-field
            readonly
            label="Unit Price"
            v-model="item.unit_price"
            prepend-icon="mdi-box"
          ></v-text-field>
          <v-text-field
            readonly
            label="Total Amount"
            :value="total_amount"
            prepend-icon="mdi-box"
          ></v-text-field>
        </div>
        <p>Destination"</p>
        <div class="d-flex">
          <v-select
            prepend-icon="mdi-forward"
            class="mr-2"
            label="From"
            :items="this.branches"
            item-text="branch_name"
            v-model="form.from_branch"
            @change="insert_branch_id('from')"
          ></v-select>
          <v-select
            prepend-icon="mdi-forward"
            label="To"
            :items="this.branches"
            item-text="branch_name"
            v-model="form.to_branch"
            @change="insert_branch_id('to')"
          ></v-select>
        </div>
        <div class="d-flex">
          <p class="mt-4 mr-4">Transfer Date:</p>
          <input
            type="date"
            v-model="form.transfer_date"
            label="Transfer Date"
          />
        </div>
        <v-textarea
          label="Notes"
          prepend-icon="mdi-pen"
          v-model="form.notes"
        ></v-textarea>
        <v-text-field
          readonly
          prepend-icon="mdi-account-circle"
          label="Processed by"
          :value="full_name"
        ></v-text-field>
      </v-card-text>
      <v-card-actions>
        <v-btn color="primary" @click="submit" :loading="loading">Submit</v-btn>
        <v-btn @click="$emit('close')"> Close</v-btn>
        <v-spacer></v-spacer>
        <p class="caption grey--text">itemTransferModal2.vue</p>
      </v-card-actions>
    </v-card>

    <!-- qyuestions -->
    <v-dialog width="450" v-model="show_question" persistent>
      <v-card>
        <v-card-title>
          <span style="text-align: center">
            Do you want to merge or add new item?</span
          >
        </v-card-title>
        <v-card-text class="d-flex justify-center">
          <v-btn color="primary" class="mr-2" @click="merge">Merge</v-btn>
          <add-item-modal
            :item_transfer="true"
            :quantity="form.quantity"
            :item="form"
            @item-added="onTransferComplete()"
          />
          <v-btn @click="show_question = false">Cancel</v-btn>
        </v-card-text>
      </v-card>
    </v-dialog>
    <!-- Merge Dialog -->
    <merge-item-modal
      :item="form"
      v-if="show_merge_dialog"
      @close="show_merge_dialog = false"
      @merged="onTransferComplete(true)"
    />
  </v-dialog>
</template>

<script>
import moment from "moment";

import { mapGetters } from "vuex";
import AddItemModal from "./addItemModal.vue";
import { mapActions } from "vuex";

export default {
  components: {
    mergeItemModal: () => import("./mergeItemModal.vue"),
    AddItemModal,
  },
  props: {
    item: { type: Object, default: () => ({}) },
    // When launched from the serial scanner, the specific serials to transfer
    // ([{ serial_number, serial_id, lot_id, batch_description }]). In this mode
    // the batches are determined by the serials — no manual batch picking.
    scannedSerials: { type: Array, default: () => [] },
  },
  data() {
    return {
      item_transfer: true,
      show_question: false,
      show_merge_dialog: false,
      dialog: true,
      loading: false,
      lotsLoading: false,
      allocations: [],
      form: {
        item_id: "",
        item_name: "",
        from_branch: "",
        to_branch: "",
        quantity: "",
        transfer_date: "",
        notes: "",
        from_branch_id: "",
        to_branch_id: "",
        amount: "",
        lot_allocations: [],
        serials: [],
      },
    };
  },
  computed: {
    ...mapGetters({
      user: "auth/user",
      branches: "branch/branches",
    }),
    full_name() {
      return this.user.full_name;
    },
    serialMode() {
      return this.scannedSerials && this.scannedSerials.length > 0;
    },
    totalAllocated() {
      return this.allocations.reduce((sum, a) => sum + (Number(a.qty) || 0), 0);
    },
    total_amount() {
      return this.item.unit_price * this.totalAllocated;
    },
  },
  created() {
    // set default values
    this.form.item_id = this.item.id;
    this.form.item_name = this.item.item_name;
    this.form.from_branch = this.user.branch.branch_name;
    this.form.transfer_date = moment().format("Y-MM-DD");

    // set items for add
    this.set_item_order_for_add(this.item);
    // set from branch id
    this.insert_branch_id("from");
    // load this item's stock lots so the user can pick which to transfer
    this.loadLots();
  },
  methods: {
    ...mapActions({
      set_item_order_for_add: "item/set_item_order_for_add",
      get_stock_lots: "item/get_stock_lots",
    }),
    async loadLots() {
      this.lotsLoading = true;
      try {
        const lots = await this.get_stock_lots(this.item.id);
        this.allocations = (lots || [])
          .filter((l) => Number(l.remaining_quantity) > 0)
          .map((l) => ({
            lot_id: l.id,
            remaining: Number(l.remaining_quantity),
            unit_cost: l.unit_cost,
            batch: l.import_batch ? l.import_batch.batch_description : "",
            qty: 0,
          }));

        // Serial-driven mode: auto-allocate one unit per scanned serial to its
        // own batch (the serials decide the batches), and carry the serial
        // numbers so the server moves exactly those units.
        if (this.serialMode) {
          let unmatched = 0;
          this.scannedSerials.forEach((s) => {
            const alloc = this.allocations.find((a) => a.lot_id === s.lot_id);
            if (alloc) alloc.qty = (Number(alloc.qty) || 0) + 1;
            else unmatched += 1;
          });
          // Serials with no (or an unavailable) batch attribution: spread the
          // leftover count across batches that still have spare capacity so the
          // total transferred quantity matches the serial count.
          for (const alloc of this.allocations) {
            if (unmatched <= 0) break;
            const spare = alloc.remaining - (Number(alloc.qty) || 0);
            if (spare > 0) {
              const take = Math.min(spare, unmatched);
              alloc.qty = (Number(alloc.qty) || 0) + take;
              unmatched -= take;
            }
          }
          this.form.serials = this.scannedSerials.map((s) => s.serial_number);
        }
      } catch (err) {
        console.error("Failed to load stock lots:", err);
        this.allocations = [];
      }
      this.lotsLoading = false;
    },
    clampAlloc(idx) {
      const a = this.allocations[idx];
      let q = Math.floor(Number(a.qty) || 0);
      if (q < 0) q = 0;
      if (q > a.remaining) q = a.remaining;
      a.qty = q;
    },
    insert_branch_id(type) {
      // Determine the correct branch and branch_id fields based on the type
      let branchNameField =
        type === "to" ? this.form.to_branch : this.form.from_branch;
      let branchIdField = type === "to" ? "to_branch_id" : "from_branch_id";

      // Find the matching branch
      let branch = this.branches.find(
        (b) => b.branch_name.toLowerCase() === branchNameField.toLowerCase()
      );

      // Check if branch exists and update the corresponding field
      if (branch) {
        this.form[branchIdField] = branch.id;
      } else {
        console.error("Branch not found for:", branchNameField);
      }
      // set total amount
      this.form.amount = this.total_amount;
      console.log(this.form);
    },
    merge() {
      this.show_merge_dialog = true;
      this.show_question = false;
      console.log(this.form);
    },
    // Called after a successful transfer (either the merge path or the
    // add-new-item path). Closes the merge/question dialogs and the transfer
    // form itself so the same transfer cannot be submitted twice, and shows a
    // success notification. The add-new path already alerts via addItemModal,
    // so `notify` is only set for the merge path to avoid a duplicate. (JMB-17)
    onTransferComplete(notify = false) {
      if (notify) {
        this.$alert({
          title: "Success",
          message: "Successfully processed the item transfer.",
          color: "success",
          icon: "mdi-check-circle",
        });
      }
      this.show_merge_dialog = false;
      this.show_question = false;
      this.$emit("close");
    },
    submit() {
      // check for blanks
      if (this.form.notes == "" || this.form.to_branch == "") {
        this.$alert({
          title: "Validation",
          message: "Please check all fields",
          color: "warning",
          icon: "mdi-alert",
        });
        return;
      }
      // cannot transfer to the same branch
      if (this.form.from_branch_id === this.form.to_branch_id) {
        this.$alert({
          title: "Validation",
          message: "Source and destination branches must be different.",
          color: "warning",
          icon: "mdi-alert",
        });
        return;
      }
      // must allocate at least 1 unit across the lots
      if (this.totalAllocated <= 0) {
        this.$alert({
          title: "Validation",
          message: "Enter the quantity to transfer for at least one batch.",
          color: "warning",
          icon: "mdi-alert",
        });
        return;
      }
      // build the lot selection + totals into the form before proceeding
      this.form.lot_allocations = this.allocations
        .filter((a) => Number(a.qty) > 0)
        .map((a) => ({ lot_id: a.lot_id, quantity: Number(a.qty) }));
      this.form.quantity = this.totalAllocated;
      this.form.amount = this.total_amount;

      this.show_question = true;
    },
  },
};
</script>

<style lang="scss" scoped></style>
